import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { Check, ArrowRight, Target, Activity, BarChart3 } from "lucide-react";

const PersonalizationSummary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Determine focus areas based on user's onboarding choices
  const getFocusAreas = () => {
    const focusAreas = [];
    
    // Based on diabetes type
    const diabetesType = user?.diabetes_type;
    if (diabetesType === "type1" || diabetesType === "type2") {
      focusAreas.push({
        icon: Target,
        label: "Carb awareness",
        description: "Understanding carbohydrate content in your meals"
      });
    }
    
    if (diabetesType === "prediabetes" || diabetesType === "type2") {
      focusAreas.push({
        icon: BarChart3,
        label: "Portion control",
        description: "Managing portion sizes for better glucose control"
      });
    }
    
    // Based on glucose monitoring preference
    if (user?.checks_glucose) {
      focusAreas.push({
        icon: Activity,
        label: "Post-meal glucose tracking",
        description: "Connecting meals to glucose patterns"
      });
    }
    
    // Ensure we always have at least 3 focus areas
    const defaultAreas = [
      {
        icon: Target,
        label: "Carb awareness",
        description: "Understanding carbohydrate content in your meals"
      },
      {
        icon: BarChart3,
        label: "Portion control",
        description: "Managing portion sizes for better glucose control"
      },
      {
        icon: Activity,
        label: "Post-meal glucose tracking",
        description: "Connecting meals to glucose patterns"
      }
    ];
    
    // Fill with defaults if we don't have enough
    while (focusAreas.length < 3) {
      const nextDefault = defaultAreas[focusAreas.length];
      if (!focusAreas.some(f => f.label === nextDefault.label)) {
        focusAreas.push(nextDefault);
      }
    }
    
    return focusAreas.slice(0, 3);
  };

  const focusAreas = getFocusAreas();

  const handleContinue = () => {
    navigate("/first-meal");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg rounded-3xl border border-orange-100/50 shadow-soft-lift p-8 space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-2">
            <Target className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-secondary">
            Your PlateMind Focus
          </h1>
          <p className="text-muted-foreground">
            Based on your answers, PlateMind will help you manage blood sugar through better meal decisions.
          </p>
        </div>

        {/* Focus Areas */}
        <div className="space-y-4">
          {focusAreas.map((area, index) => {
            const Icon = area.icon;
            return (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-2xl bg-orange-50/50 border border-orange-100/50"
              >
                <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-success" />
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-secondary">{area.label}</p>
                  <p className="text-sm text-muted-foreground">{area.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <Button
          onClick={handleContinue}
          className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary hover:shadow-button-primary-hover transition-all"
          data-testid="personalization-continue-btn"
        >
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Card>
    </div>
  );
};

export default PersonalizationSummary;
